import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import Navigation from "../../components/Navigation";

const PLAN_ROUTES = {
  starter: "/contractors/signup?plan=starter",
  pro: "/contractors/signup?plan=pro",
  territory_king: "/contractors/signup?plan=territory_king",
};

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "$44/mo",
    tagline: "Best for contractors getting started with a focused service area.",
    badge: null,
    highlighted: false,
    features: [
      "Access to BuildBright contractor portal",
      "1 service area ZIP code",
      "Email project notifications",
      "View relevant project opportunities",
      "Use claim credits only when unlocking project details",
      "Invalid contact details reviewed for replacement",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: "$89/mo",
    tagline: "Best for growing contractors who want faster alerts and broader coverage.",
    badge: "MOST POPULAR",
    highlighted: true,
    features: [
      "Everything in Starter",
      "Up to 3 service area ZIP codes",
      "Email + SMS project notifications",
      "Expanded project request visibility",
      "SMS notifications for new matching request previews",
      "Use claim credits only when unlocking project details",
      "Invalid contact details reviewed for replacement",
    ],
  },
  {
    id: "territory_king",
    name: "Territory King",
    price: "$143/mo",
    tagline: "Premium tier for established contractors seeking market dominance.",
    badge: "PREMIUM",
    highlighted: false,
    features: [
      "Everything in Pro",
      "Up to 10 service area ZIP codes",
      "Priority project notifications",
      "Premium contractor portal access",
      "Enhanced project preview system",
      "Priority customer support",
      "Certified professional status",
      "Advanced project matching",
    ],
  },
];

const TRUST_POINTS = [
  { title: "Preview first", text: "Review project type, area, urgency, and homeowner notes before using claim credits." },
  { title: "Control coverage", text: "Choose the ZIP codes and services that match your business coverage." },
  { title: "Unlock by choice", text: "Use claim credits only when you choose to unlock homeowner contact details." },
];

const AFTER_SIGNUP_STEPS = [
  "Create your contractor account",
  "Choose your plan and complete checkout",
  "Set your service area and project categories",
  "Review project opportunity previews in the portal",
  "Use claim credits only when a project looks worth unlocking",
];

const PLAN_COMPARISON = [
  { label: "Monthly access", starter: "$44/mo", pro: "$89/mo", territory_king: "$143/mo" },
  { label: "Service area coverage", starter: "1 ZIP code", pro: "Up to 3 ZIP codes", territory_king: "Up to 10 ZIP codes" },
  { label: "Notifications", starter: "Email", pro: "Email + SMS", territory_king: "Priority + SMS" },
  { label: "Request notification options", starter: "Email", pro: "Email + SMS", territory_king: "Priority + SMS" },
  { label: "Claim credits", starter: "Used only when unlocking details", pro: "Used only when unlocking details", territory_king: "Premium scoring + details" },
];

export default function Pricing() {
  const navigate = useNavigate();
  
  const handlePlanSelect = (planId) => {
    navigate(PLAN_ROUTES[planId]);
  };
  
  useEffect(() => {
    document.title = "BuildBright Contractor Plans | Project Opportunity Access";
  }, []);

  return (
    <>
      <Helmet>
        <title>BuildBright Contractor Plans | Project Opportunity Access</title>
        <meta
          name="description"
          content="Join BuildBright for access to relevant homeowner project opportunities. Simple monthly access plus claim credits when you choose to unlock project details."
        />
      </Helmet>
      <div className="min-h-screen bg-black text-white">
      <Navigation />

      {/* URGENCY BANNER */}
      <div className="w-full bg-orange-500 text-black text-center py-3 px-4 font-bold text-sm tracking-wide">
        CURRENT PRICING IS LIVE · Starter $44/mo · Pro $89/mo · Territory King $143/mo
      </div>

      <div className="max-w-5xl mx-auto px-4 py-20">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-orange-400/80 text-center mb-3">
          Contractor Plans
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 leading-tight">
          Join BuildBright for Project Requests Nationwide
        </h1>
        <p className="text-zinc-300 text-center max-w-2xl mx-auto mb-4 text-base md:text-lg">
          Get contractor portal access, review project request previews, and choose which opportunities fit your service area and business.
        </p>
        <p className="text-zinc-400 text-center max-w-2xl mx-auto mb-6">
          Monthly plans give you portal access and project visibility. Claim credits are used separately only when you choose to unlock homeowner contact details.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto mb-14">
          <div className="rounded-2xl border border-white/10 bg-zinc-950 px-4 py-4 text-center">
            <div className="text-orange-400 font-bold text-lg">Preview first</div>
            <div className="text-zinc-400 text-xs mt-1">Review details before unlocking</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-950 px-4 py-4 text-center">
            <div className="text-orange-400 font-bold text-lg">Control coverage</div>
            <div className="text-zinc-400 text-xs mt-1">Choose ZIP codes and trades</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-950 px-4 py-4 text-center">
            <div className="text-orange-400 font-bold text-lg">No outcome promise</div>
            <div className="text-zinc-400 text-xs mt-1">Contractors decide what to unlock</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={[
                "rounded-2xl border p-8 flex flex-col",
                plan.highlighted
                  ? "border-orange-400/70 bg-zinc-900"
                  : "border-white/10 bg-zinc-900",
              ].join(" ")}
            >
              {plan.badge && (
                <span className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-3">
                  {plan.badge}
                </span>
              )}
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{plan.name}</h2>
              </div>
              {plan.id === 'pro' && (
                <p className="text-[11px] text-orange-400/80 leading-tight mt-1 mb-2">
                  Best value for contractors ready to review more matching project request previews across multiple ZIP codes.
                </p>
              )}
              <p className="text-4xl font-bold text-orange-500 my-3">{plan.price}</p>
              <p className="text-sm text-zinc-400 mb-6">{plan.tagline}</p>

              <ul className="space-y-2 text-sm text-zinc-300 flex-1 mb-8">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">✓</span> {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanSelect(plan.id)}
                className={[
                  "block w-full text-center py-3 px-6 rounded-xl font-semibold text-sm transition cursor-pointer",
                  plan.highlighted
                    ? "bg-orange-500 hover:bg-orange-400 text-white"
                    : "bg-white/10 hover:bg-white/20 text-white",
                ].join(" ")}
              >
                Start Contractor Access
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-zinc-500 text-sm mt-8">
          Adjust or upgrade your plan any time. No long-term contracts.
        </p>

        <div className="mt-16 rounded-2xl border border-white/10 bg-zinc-950 px-6 py-5 text-center text-sm text-zinc-300">
          Pay monthly for access. Use claim credits only when unlocking project details.
          <div className="mt-2 text-zinc-400">
            You’ll see the credit requirement before unlocking any project opportunity.
          </div>
        </div>

        <div className="mt-20">
          <h2 className="text-2xl font-bold text-center mb-10">Built for Contractor Control</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TRUST_POINTS.map((item) => (
              <div key={item.title} className="bg-zinc-900 border border-white/10 rounded-2xl p-6">
                <p className="font-semibold text-white mb-3">{item.title}</p>
                <p className="text-zinc-300 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>


        <div className="mt-20 rounded-3xl border border-white/10 bg-zinc-950 p-6 md:p-8">
          <h2 className="text-2xl font-bold text-center mb-8">What Happens After Signup</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {AFTER_SIGNUP_STEPS.map((step, index) => (
              <div key={step} className="rounded-2xl border border-white/10 bg-zinc-900 p-4">
                <div className="text-orange-400 text-xs font-bold mb-2">STEP {index + 1}</div>
                <p className="text-zinc-200 text-sm leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 rounded-3xl border border-white/10 bg-zinc-950 p-6 md:p-8">
          <h2 className="text-2xl font-bold text-center mb-8">Starter vs Pro vs Territory King</h2>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            {PLAN_COMPARISON.map((row) => (
              <div key={row.label} className="grid grid-cols-4 border-b border-white/10 last:border-b-0 text-sm">
                <div className="bg-zinc-900 p-4 text-zinc-300 font-medium">{row.label}</div>
                <div className="p-4 text-zinc-400">{row.starter}</div>
                <div className="p-4 text-white">{row.pro}</div>
                <div className="p-4 text-orange-400 font-semibold">{row.territory_king}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-4">Project Opportunity Claim Credits</h2>
          <p className="text-center text-zinc-400 text-sm mb-3 max-w-2xl mx-auto">
            Claim credit use may vary by project type, urgency, and potential project scope. You’ll always see the credit requirement before unlocking homeowner contact details.
          </p>
          <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-300">
            <div className="rounded-2xl border border-white/10 bg-zinc-900 p-5">Standard residential project: 1 credit</div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900 p-5">Urgent repair request: 1–2 credits</div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900 p-5">Higher-scope project request: 2+ credits</div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900 p-5">Multi-trade project request: 2+ credits</div>
          </div>
        </div>

        <div className="mt-16 rounded-2xl border border-orange-400/30 bg-orange-500/10 px-6 py-6 text-center">
          <p className="text-zinc-100 font-semibold mb-2">Ready to review project opportunity previews?</p>
          <p className="text-zinc-400 text-sm mb-4">Start with monthly access. Use claim credits only when a project looks worth unlocking.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => handlePlanSelect('starter')}
              className="inline-block bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl font-semibold text-sm transition cursor-pointer"
            >
              Start with Starter
            </button>
            <button
              onClick={() => handlePlanSelect('pro')}
              className="inline-block bg-orange-500 hover:bg-orange-400 text-white px-6 py-3 rounded-xl font-semibold text-sm transition cursor-pointer"
            >
              Choose Pro
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-4">BuildBright supports project intake and contractor marketing visibility. BuildBright does not sell guaranteed jobs, guaranteed customers, or pay-per-lead promises. Subscription gives contractor access, visibility, and a claim-based workflow for reviewing project requests.</p>
        </div>
      </div>
      </div>
    </>
  );
}
